import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HeroSection() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const x = (e.clientX / window.innerWidth - 0.5) * 24;
    const y = (e.clientY / window.innerHeight - 0.5) * 24;
    setMousePos({ x, y });
  }, []);

  const scrollToServices = () => {
    const el = document.getElementById('services');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // The homepage has no "projects" section — send visitors to the portfolio page.
  const goToPortfolio = () => {
    navigate('/portfolio');
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <picture className="absolute inset-0">
        <source media="(max-width: 640px)" srcSet="/images/home-hero-768.webp" />
        <img
          src="/images/home-hero-1600.webp"
          alt=""
          width="1600"
          height="900"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </picture>

      <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/65 via-charcoal-900/45 to-charcoal-900/75" />

      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(201,122,74,0.5) 3px, rgba(201,122,74,0.5) 4px), repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(201,122,74,0.5) 3px, rgba(201,122,74,0.5) 4px)',
        }}
      />

      <div className="relative z-10 text-center px-6 max-w-5xl w-full">
        <p className="font-mono text-xs text-terracotta-400 tracking-[0.3em] uppercase mb-8 animate-fade-in-up whitespace-nowrap">
          Melbourne, Australia
        </p>

        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-cream-50 mb-8 leading-tight tracking-tight animate-fade-in-up">
          Building Design<br />Studio Melbourne
        </h1>

        <p className="text-base md:text-lg lg:text-xl text-cream-100/85 font-light leading-relaxed max-w-3xl mx-auto mb-6 animate-fade-in-up-delay">
          Townhouse, custom home, commercial and interior design — connected from first idea to delivery.
        </p>

        <p className="text-sm text-cream-100/55 mb-12 max-w-2xl mx-auto leading-relaxed animate-fade-in-up-delay">
          Over 15 years coordinating design, documentation and project delivery across greater Melbourne.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up-delay-2">
          <button
            onClick={scrollToServices}
            className="px-10 py-4 bg-terracotta-500 text-cream-50 text-sm font-medium tracking-wider uppercase hover:bg-terracotta-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Explore Services
          </button>
          <button
            onClick={goToPortfolio}
            className="px-10 py-4 border border-cream-100/30 text-cream-100 text-sm font-medium tracking-wider uppercase hover:border-cream-100/60 hover:text-cream-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            View Projects
          </button>
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-fade-in-up-delay-2">
        <div className="w-5 h-8 border border-cream-100/25 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1 h-1.5 bg-terracotta-400 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}
